import { useState } from 'react';

import type { ActionCardField } from '@littlebird/action-cards';

const isFieldShown = (
  field: ActionCardField,
  editing: boolean,
  revealed: ReadonlySet<string>
) =>
  !field.hideWhenEmpty ||
  Boolean(field.value) ||
  (editing && revealed.has(field.key));

export const useRevealedFields = (
  fields: readonly ActionCardField[],
  editing: boolean
) => {
  const [revealed, setRevealed] = useState(() => new Set<string>());
  const [revealedFocusKey, setRevealedFocusKey] = useState<string>();
  const [wasEditing, setWasEditing] = useState(editing);

  // Reveals belong to one edit session. Reset them as the session ends so the
  // next one starts from the card's saved shape and nothing pops back in.
  if (wasEditing !== editing) {
    setWasEditing(editing);
    if (!editing) {
      setRevealed(new Set());
      setRevealedFocusKey(undefined);
    }
  }

  const revealField = (key: string) => {
    setRevealed((previous) => {
      if (previous.has(key)) return previous;
      const next = new Set(previous);
      next.add(key);
      return next;
    });
    setRevealedFocusKey(key);
  };

  return {
    visibleFields: fields.filter((field) =>
      isFieldShown(field, editing, revealed)
    ),
    hiddenEditableFields: editing
      ? fields.filter(
          (field) =>
            field.editable &&
            field.hideWhenEmpty &&
            !field.value &&
            !revealed.has(field.key)
        )
      : [],
    revealField,
    revealedFocusKey,
  };
};
